import { createClient } from '@supabase/supabase-js';

// Validation des variables d'environnement
if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
  throw new Error('Les variables d\'environnement Supabase ne sont pas configurées');
}

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Configuration des timeouts et options de sécurité
const options = {
  auth: {
    persistSession: true,
    detectSessionInUrl: true,
    autoRefreshToken: true,
    storageKey: 'pointage_auth_token'
  },
  global: {
    headers: {
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains'
    },
  },
  db: {
    schema: 'public',
    timeout: 10000, // 10 secondes timeout
  },
  realtime: {
    reconnectAfterMs: attempt => Math.min(1000 * Math.exp(attempt), 30000),
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, options);

// Fonction utilitaire de validation des entrées
export const sanitizeInput = (input) => {
  if (typeof input === 'string') {
    return input.replace(/[<>'"]/g, ''); // Protection XSS basique
  }
  return input;
};

// Wrapper sécurisé pour les requêtes
export const fetchData = async (table, filters = {}, options = {}) => {
  try {
    // Validation du nom de la table
    if (!/^[a-zA-Z0-9_]+$/.test(table)) {
      throw new Error('Nom de table invalide');
    }

    // Validation des filtres
    Object.keys(filters).forEach(key => {
      if (!/^[a-zA-Z0-9_]+$/.test(key)) {
        throw new Error('Clé de filtre invalide');
      }
      filters[key] = sanitizeInput(filters[key]);
    });

    let query = supabase.from(table);
    
    query = query.select(options.select || '*');
    
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    if (options.order) {
      const column = sanitizeInput(options.order.column);
      query = query.order(column, { 
        ascending: options.order.ascending ?? true 
      });
    }

    // Ajout de timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Timeout')), options.timeout || 10000);
    });

    const result = await Promise.race([query, timeoutPromise]);
    return result;

  } catch (error) {
    console.error('Erreur sécurisée:', error.message);
    throw new Error('Erreur lors de la récupération des données');
  }
};

export const createRecord = async (table, record) => {
  const { data, error } = await supabase
    .from(table)
    .insert(record)
    .select();

  if (error) throw error;
  return data;
};

export const updateRecord = async (table, id, updates) => {
  const { data, error } = await supabase
    .from(table)
    .update(updates)
    .eq('id', id)
    .select();

  if (error) throw error;
  return data;
};

export const deleteRecord = async (table, id) => {
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export default supabase;
